import { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import TextureRounded from '@mui/icons-material/TextureRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import PublishRounded from '@mui/icons-material/PublishRounded';
import UnpublishedRounded from '@mui/icons-material/UnpublishedRounded';
import FileDownloadRounded from '@mui/icons-material/FileDownloadRounded';
import PageHeader from '../components/common/PageHeader';
import storeLabel from '../utils/storeLabel';
import FabricCollectionsDialog from '../components/fabrics/FabricCollectionsDialog';
import exportRowsToXlsx from '../utils/exportXlsx';
import ImageUploader from '../components/common/ImageUploader';
import PushResultsDialog from '../components/common/PushResultsDialog';
import { useStores } from '../context/StoresContext';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import {
  getFabricTypes, createFabricType, deleteFabricType,
  getFabricPatterns, getFabricPattern, createFabricPattern, updateFabricPattern, deleteFabricPattern,
  updatePatternManufacturers, updatePatternCoatings, updatePatternFeatures, updatePatternStoreStatus,
  importFabrics, exportFabricsJson,
  getFabricColors, createFabricColor, updateFabricColor, deleteFabricColor,
  bulkUpdatePatternStoreStatus, bulkDeletePatterns, pushFabricPattern, updatePatternShopifyStatus,
  getFabricCollections, updatePatternCollections, addPatternsToCollection, createFabricCollection,
} from '../services/fabricsApi';
import { getManufacturers } from '../services/manufacturersApi';
import { getFabricCoatings } from '../services/fabricCoatingsApi';
import { getFabricGrades } from '../services/fabricGradesApi';
import { getFeatures } from '../services/featuresApi';
import { pushFabricsAndSync } from '../services/syncApi';
import { getLog } from '../services/logsApi';

const PATTERN_DETAIL_FIELDS = [
  { key: 'content', label: 'Content' },
  { key: 'backing', label: 'Backing' },
  { key: 'finish', label: 'Finish' },
  { key: 'direction', label: 'Direction' },
  { key: 'repeat', label: 'Repeat' },
  { key: 'weight', label: 'Weight' },
  { key: 'width', label: 'Width' },
  { key: 'abrasion', label: 'Abrasion' },
  { key: 'physical_properties', label: 'Physical Properties' },
  { key: 'light_fastness', label: 'Light Fastness' },
  { key: 'color_fastness', label: 'Color Fastness' },
  { key: 'flammability', label: 'Flammability' },
  { key: 'cleaning', label: 'Cleaning' },
];

const Fabrics = () => {
  const { activeStores } = useStores();
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [allManufacturers, setAllManufacturers] = useState([]);
  const [allCoatings, setAllCoatings] = useState([]);
  const [allGrades, setAllGrades] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);

  const [expandedTypes, setExpandedTypes] = useState({});
  const [expandedPatterns, setExpandedPatterns] = useState({});
  const [colorsByPattern, setColorsByPattern] = useState({});

  const [typeDialog, setTypeDialog] = useState(false);
  const [patternDialog, setPatternDialog] = useState({ open: false, typeId: null });
  const [colorDialog, setColorDialog] = useState({ open: false, patternId: null });
  const [editDialog, setEditDialog] = useState({ open: false, pattern: null });
  const [editColorDialog, setEditColorDialog] = useState({ open: false, color: null, patternId: null });

  const [formName, setFormName] = useState('');
  const [formImage, setFormImage] = useState('');
  const [formColorCode, setFormColorCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  // Per-(pattern, store) Shopify status change in flight — disables the
  // Select while the PUT to /shopify-status is round-tripping.
  const [updatingShopifyStatus, setUpdatingShopifyStatus] = useState(null);

  const [editForm, setEditForm] = useState({});
  const [editMfrs, setEditMfrs] = useState([]);
  const [editCoatings, setEditCoatings] = useState([]);
  const [editFeatures, setEditFeatures] = useState([]);
  const [allCollections, setAllCollections] = useState([]);
  const [editCollections, setEditCollections] = useState([]);
  const [addCollDialog, setAddCollDialog] = useState(false);
  const [addCollTarget, setAddCollTarget] = useState(null); // existing collection object
  const [addCollNewName, setAddCollNewName] = useState(''); // new collection name
  const [assigningColl, setAssigningColl] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [editStoreStatuses, setEditStoreStatuses] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);


  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterManufacturer, setFilterManufacturer] = useState(null);
  const [filterStore, setFilterStore] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');

  // Selection state
  const [selectedPatterns, setSelectedPatterns] = useState(new Set());
  const [bulkStore, setBulkStore] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [pushResults, setPushResults] = useState({ open: false, results: [] });
  const [importing, setImporting] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        getFabricTypes(),
        getFabricPatterns(),
        getManufacturers(),
        getFabricCoatings(),
        getFabricGrades(),
        getFeatures(),
        getFabricCollections(),
      ]);

      if (results[0].status === 'fulfilled') setTypes(results[0].value.items);
      if (results[1].status === 'fulfilled') setPatterns(results[1].value.items);
      if (results[2].status === 'fulfilled') setAllManufacturers(results[2].value.items);
      if (results[3].status === 'fulfilled') setAllCoatings(results[3].value.items);
      if (results[4].status === 'fulfilled') setAllGrades(results[4].value.items || []);
      if (results[5].status === 'fulfilled') setAllFeatures(results[5].value.items || []);
      if (results[6].status === 'fulfilled') setAllCollections(results[6].value.items || []);

      results.forEach((r, idx) => {
        if (r.status === 'rejected') {
          const name = ['types', 'patterns', 'manufacturers', 'coatings', 'grades', 'features'][idx];
          console.error(`Failed to fetch ${name}:`, r.reason);
        }
      });
    } catch (err) {
      console.error('Failed to fetch fabrics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleType = (typeId) => setExpandedTypes((prev) => ({ ...prev, [typeId]: !prev[typeId] }));

  const togglePattern = async (patternId) => {
    const isExpanding = !expandedPatterns[patternId];
    setExpandedPatterns((prev) => ({ ...prev, [patternId]: !prev[patternId] }));
    if (isExpanding && !colorsByPattern[patternId]) {
      try {
        const data = await getFabricColors(patternId);
        setColorsByPattern((prev) => ({ ...prev, [patternId]: data.items }));
      } catch (err) { console.error('Failed to fetch colors:', err); }
    }
  };

  const handleCreateType = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      await createFabricType(formName.trim());
      setTypeDialog(false); setFormName('');
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create');
    }
    finally { setSaving(false); }
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm('Delete this fabric type and all its patterns/colors?')) return;
    setDeleting(`type-${id}`);
    try { await deleteFabricType(id); await fetchAll(); }
    catch (err) { console.error('Delete failed:', err); }
    finally { setDeleting(null); }
  };

  const handleCreatePattern = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      await createFabricPattern({ fabric_type_id: patternDialog.typeId, name: formName.trim() });
      setPatternDialog({ open: false, typeId: null }); setFormName('');
      await fetchAll();
    } catch (err) { setError(err.response?.data?.message || 'Failed to create'); }
    finally { setSaving(false); }
  };

  // Change a pattern's Shopify status on a specific store directly from the
  // list row (Active / Draft / Archived). Wraps the same endpoint the edit
  // dialog uses; refetch after to surface the new value.
  const handleShopifyStatusChangeForPattern = async (patternId, storeId, newStatus) => {
    setUpdatingShopifyStatus(`${patternId}-${storeId}`);
    try {
      await updatePatternShopifyStatus(patternId, storeId, newStatus);
      await fetchAll();
    } catch (err) {
      console.error('Pattern status change failed:', err);
    } finally {
      setUpdatingShopifyStatus(null);
    }
  };

  const handleDeletePattern = async (id) => {
    if (!window.confirm('Delete this pattern and all its colors?')) return;
    setDeleting(`pattern-${id}`);
    try { await deleteFabricPattern(id); await fetchAll(); }
    catch (err) { console.error('Delete failed:', err); }
    finally { setDeleting(null); }
  };


  const openEditPattern = async (patternId) => {
    try {
      const data = await getFabricPattern(patternId);
      const p = data.item;
      const form = { name: p.name, grade: p.grade, fabric_type_id: p.fabric_type_id, fabric_grade_id: p.fabric_grade_id || null, status: p.status || 'ready' };
      for (const f of PATTERN_DETAIL_FIELDS) form[f.key] = p[f.key] || '';
      setEditForm(form);
      setEditMfrs(p.manufacturers.map((m) => m.id));
      setEditCoatings(p.coatings.map((c) => c.id));
      setEditFeatures((p.features || []).map((f) => f.id));
      setEditCollections((p.collections || []).map((c) => c.id));
      setEditStoreStatuses(p.store_statuses || []);
      setEditDialog({ open: true, pattern: p });
    } catch (err) { console.error('Failed to load pattern:', err); }
  };

  const handleSaveEdit = async () => {
    if (!editDialog.pattern) return;
    const pid = editDialog.pattern.id;
    setSavingEdit(true);
    // Run each save step independently so one failing step (e.g. store status)
    // can't abort the rest — most importantly, it must never block the
    // collection auto-push or leave the dialog stuck open.
    const step = async (label, fn) => {
      try { return await fn(); } catch (e) { console.error(`Edit save step "${label}" failed:`, e); return null; }
    };
    await step('pattern', () => updateFabricPattern(pid, editForm));
    await step('manufacturers', () => updatePatternManufacturers(pid, editMfrs));
    await step('coatings', () => updatePatternCoatings(pid, editCoatings));
    await step('features', () => updatePatternFeatures(pid, editFeatures));
    const collRes = await step('collections', () => updatePatternCollections(pid, editCollections));
    await step('store-status', () => updatePatternStoreStatus(pid, editStoreStatuses.map((s) => ({ store_id: s.store_id, status: s.status }))));
    setSavingEdit(false);
    setEditDialog({ open: false, pattern: null });
    await fetchAll();
    // If collection membership changed, push this pattern to the affected
    // products' live stores and sync them.
    if (collRes?.product_ids?.length) await autoSyncProducts(collRes.product_ids, [pid]);
  };

  const handleCreateColor = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    const pid = colorDialog.patternId;
    try {
      await createFabricColor({ fabric_pattern_id: pid, name: formName.trim(), image_url: formImage.trim(), color_code: formColorCode.trim() });
      setColorDialog({ open: false, patternId: null }); setFormName(''); setFormImage(''); setFormColorCode('');
      const data = await getFabricColors(pid);
      setColorsByPattern((prev) => ({ ...prev, [pid]: data.items }));
      await fetchAll();
    } catch (err) { setError(err.response?.data?.message || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const handleDeleteColor = async (id, patternId) => {
    if (!window.confirm('Delete this color?')) return;
    setDeleting(`color-${id}`);
    try {
      await deleteFabricColor(id);
      const data = await getFabricColors(patternId);
      setColorsByPattern((prev) => ({ ...prev, [patternId]: data.items }));
      await fetchAll();
    } catch (err) { console.error('Delete failed:', err); }
    finally { setDeleting(null); }
  };

  const openEditColor = (color, patternId) => {
    setEditColorDialog({ open: true, color, patternId });
    setFormName(color.name);
    setFormImage(color.image_url || '');
    setFormColorCode(color.color_code || '');
    setError('');
  };

  const handleSaveColor = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    const { color, patternId } = editColorDialog;
    try {
      await updateFabricColor(color.id, { name: formName.trim(), image_url: formImage.trim(), color_code: formColorCode.trim() });
      setEditColorDialog({ open: false, color: null, patternId: null });
      setFormName(''); setFormImage(''); setFormColorCode('');
      const data = await getFabricColors(patternId);
      setColorsByPattern((prev) => ({ ...prev, [patternId]: data.items }));
    } catch (err) { setError(err.response?.data?.message || 'Failed to update'); }
    finally { setSaving(false); }
  };

  // Filtered patterns logic - applies search, manufacturer, and store status filters
  const filteredPatternIds = useMemo(() => {
    let filtered = patterns;

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((p) => p.name.toLowerCase().includes(q));
    }

    // Manufacturer filter
    if (filterManufacturer) {
      filtered = filtered.filter((p) =>
        p.manufacturers.some((m) => m.id === filterManufacturer.id)
      );
    }

    // Store + status filter
    if (filterStore && filterStatus !== 'All') {
      const targetStatus = filterStatus === 'Published' ? 'active' : 'inactive';
      filtered = filtered.filter((p) => {
        const ss = (p.store_statuses || []).find((s) => s.store_id === filterStore.id);
        if (targetStatus === 'active') {
          return ss?.status === 'active';
        } else {
          return !ss || ss.status !== 'active';
        }
      });
    }

    return new Set(filtered.map((p) => p.id));
  }, [patterns, searchQuery, filterManufacturer, filterStore, filterStatus]);

  const patternsForType = (typeId) =>
    patterns.filter((p) => p.fabric_type_id === typeId && filteredPatternIds.has(p.id));

  // All visible (filtered) pattern IDs across all types
  const allVisiblePatternIds = useMemo(() => {
    return patterns.filter((p) => filteredPatternIds.has(p.id)).map((p) => p.id);
  }, [patterns, filteredPatternIds]);

  // Selection helpers
  const togglePatternSelection = (patternId) => {
    setSelectedPatterns((prev) => {
      const next = new Set(prev);
      if (next.has(patternId)) next.delete(patternId);
      else next.add(patternId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allSelected = allVisiblePatternIds.length > 0 && allVisiblePatternIds.every((id) => selectedPatterns.has(id));
    if (allSelected) {
      setSelectedPatterns(new Set());
    } else {
      setSelectedPatterns(new Set(allVisiblePatternIds));
    }
  };

  const clearSelection = () => setSelectedPatterns(new Set());

  const handleBulkPublish = async () => {
    if (!bulkStore || selectedPatterns.size === 0) return;
    // Warn before overriding existing Shopify products.
    const allPatterns = (types || []).flatMap((t) => t.patterns || []);
    const selectedList = Array.from(selectedPatterns)
      .map((id) => allPatterns.find((p) => p.id === id))
      .filter(Boolean);
    const existing = selectedList.filter((p) => (p.pushed_counts?.[bulkStore.id] || 0) > 0);
    if (existing.length > 0) {
      const names = existing.slice(0, 5).map((p) => p.name).join(', ');
      const more = existing.length > 5 ? `, +${existing.length - 5} more` : '';
      const totalColors = existing.reduce((sum, p) => sum + (p.pushed_counts?.[bulkStore.id] || 0), 0);
      if (!window.confirm(
        `${existing.length} pattern(s) already live on ${storeLabel(bulkStore)} (${totalColors} Shopify product(s) total): ${names}${more}.\n\n` +
        `Pushing will OVERRIDE the existing Shopify products and metafields. Continue?`
      )) return;
    }
    setBulkProcessing(true);
    const results = [];
    try {
      const ids = Array.from(selectedPatterns);
      setBulkProgress({ done: 0, total: ids.length });
      // Push with limited concurrency + live progress so a large batch (e.g. 178
      // patterns) finishes faster and never just sits on a blank spinner. A hung
      // push can't stall everything — the server times out each Shopify call.
      let next = 0;
      let done = 0;
      const CONCURRENCY = 4;
      const worker = async () => {
        while (next < ids.length) {
          const patId = ids[next++];
          const pat = allPatterns.find((p) => p.id === patId);
          try {
            const data = await pushFabricPattern(patId, bulkStore.id);
            const errors = data?.errors || [];
            const products = data?.products || [];
            results.push({
              name: pat?.name || `Pattern ${patId}`,
              status: errors.length === 0 ? 'ok' : (products.length > 0 ? 'partial' : 'error'),
              message: data?.message,
              products,
              errors,
            });
          } catch (err) {
            results.push({
              name: pat?.name || `Pattern ${patId}`,
              status: 'error',
              message: err.response?.data?.message || err.message,
              errors: [{ field: 'request', message: err.response?.data?.detail || err.message }],
            });
          }
          done += 1;
          setBulkProgress({ done, total: ids.length });
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, () => worker()));
      setSelectedPatterns(new Set());
      await fetchAll();
      setPushResults({ open: true, results });
    } catch (err) { console.error('Bulk push failed:', err); }
    finally { setBulkProcessing(false); setBulkProgress({ done: 0, total: 0 }); }
  };

  const handleBulkUnpublish = async () => {
    if (!bulkStore || selectedPatterns.size === 0) return;
    setBulkProcessing(true);
    try {
      await bulkUpdatePatternStoreStatus(Array.from(selectedPatterns), bulkStore.id, 'inactive');
      setSelectedPatterns(new Set());
      await fetchAll();
    } catch (err) { console.error('Bulk unpublish failed:', err); }
    finally { setBulkProcessing(false); }
  };

  const handleBulkDelete = async () => {
    if (selectedPatterns.size === 0) return;
    if (!window.confirm(`Delete ${selectedPatterns.size} selected pattern(s) and all their colors? This cannot be undone.`)) return;
    setBulkProcessing(true);
    try {
      await bulkDeletePatterns(Array.from(selectedPatterns));
      setSelectedPatterns(new Set());
      await fetchAll();
    } catch (err) { console.error('Bulk delete failed:', err); }
    finally { setBulkProcessing(false); }
  };

  // Poll a background push/sync job and flip the message to "pushed" when done.
  const pollPushLog = (logId) => {
    const deadline = Date.now() + 120000;
    const tick = async () => {
      let log = null;
      try { log = await getLog(logId); } catch { /* keep polling */ }
      const st = log?.status;
      if (st === 'done' || st === 'error') {
        setSnack({
          open: true,
          message: st === 'error' ? 'Push finished with errors — check the Logs page.' : 'Pushed successfully to your stores.',
          severity: st === 'error' ? 'warning' : 'success',
        });
        return;
      }
      if (Date.now() < deadline) setTimeout(tick, 2500);
    };
    tick();
  };

  // After a collection change, push the just-added fabrics to the stores where
  // affected products are live, then re-sync those products so the new fabrics
  // actually appear on the storefront. Shows live "pushing → pushed" status.
  const autoSyncProducts = async (productIds, patternIds) => {
    if (!productIds || productIds.length === 0) {
      setSnack({ open: true, message: 'Saved. No products use this collection yet — nothing to sync.', severity: 'info' });
      return;
    }
    try {
      const res = await pushFabricsAndSync({ patternIds: patternIds || [], productIds });
      if (res?.stores && res?.log_id) {
        setSnack({ open: true, message: res.message || 'Pushing to your stores…', severity: 'info' });
        pollPushLog(res.log_id);
      } else {
        setSnack({ open: true, message: res?.message || 'Saved.', severity: 'info' });
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'sync could not start';
      setSnack({ open: true, message: `Saved, but auto-sync didn't start (${msg}). You can push manually.`, severity: 'warning' });
    }
  };

  const handleAddToCollection = async () => {
    if (selectedPatterns.size === 0) return;
    const name = addCollNewName.trim();
    if (!addCollTarget && !name) return; // need either an existing collection or a new name
    setAssigningColl(true);
    try {
      let collectionId = addCollTarget?.id;
      if (!collectionId) {
        const { item } = await createFabricCollection({ name });
        collectionId = item.id;
      }
      const addedPatternIds = Array.from(selectedPatterns);
      const { product_ids } = await addPatternsToCollection(collectionId, addedPatternIds);
      setAddCollDialog(false);
      setAddCollTarget(null);
      setAddCollNewName('');
      setSelectedPatterns(new Set());
      await fetchAll();
      await autoSyncProducts(product_ids, addedPatternIds);
    } catch (err) {
      console.error('Add to collection failed:', err);
      alert(err.response?.data?.message || 'Failed to add to collection');
    } finally { setAssigningColl(false); }
  };

  const getStoreStatusForPattern = (pattern, storeId) => {
    const ss = (pattern.store_statuses || []).find((s) => s.store_id === storeId);
    return ss?.status === 'active';
  };

  const allVisibleSelected = allVisiblePatternIds.length > 0 && allVisiblePatternIds.every((id) => selectedPatterns.has(id));
  const someVisibleSelected = allVisiblePatternIds.some((id) => selectedPatterns.has(id)) && !allVisibleSelected;

  if (loading) {
    return (
      <Box>
        <PageHeader title="Fabrics" subtitle="Manage fabric types, patterns, and colors" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <input
        id="fabrics-import-file"
        type="file"
        accept=".csv,.tsv,text/csv,text/tab-separated-values"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setImporting(true);
          try {
            const text = await file.text();
            // Parse CSV properly handling quoted fields with embedded newlines and commas
            const parseCSV = (csvText) => {
              const rows = [];
              let row = [];
              let field = '';
              let inQuotes = false;
              for (let i = 0; i < csvText.length; i++) {
                const ch = csvText[i];
                if (inQuotes) {
                  if (ch === '"') {
                    if (i + 1 < csvText.length && csvText[i + 1] === '"') {
                      field += '"'; i++; // escaped quote
                    } else {
                      inQuotes = false; // end of quoted field
                    }
                  } else {
                    field += ch;
                  }
                } else {
                  if (ch === '"') {
                    inQuotes = true;
                  } else if (ch === ',') {
                    row.push(field.trim()); field = '';
                  } else if (ch === '\n' || ch === '\r') {
                    if (ch === '\r' && csvText[i + 1] === '\n') i++; // skip \n after \r
                    row.push(field.trim()); field = '';
                    if (row.some((c) => c !== '')) rows.push(row);
                    row = [];
                  } else {
                    field += ch;
                  }
                }
              }
              // last field/row
              row.push(field.trim());
              if (row.some((c) => c !== '')) rows.push(row);
              return rows;
            };
            const allRows = parseCSV(text);
            if (allRows.length < 2) throw new Error('No data rows');
            // Find the actual header row: look for a row that starts with "_id" or contains "pattern"
            let headerIdx = 0;
            for (let i = 0; i < Math.min(allRows.length, 5); i++) {
              const firstCell = (allRows[i][0] || '').toLowerCase();
              const rowLower = allRows[i].map((c) => c.toLowerCase());
              if (firstCell === '_id' || rowLower.includes('pattern')) {
                headerIdx = i;
                break;
              }
            }
            const headers = allRows[headerIdx].map((h) => h.toLowerCase().trim());
            console.log('Header row index:', headerIdx, 'Headers:', headers);
            const items = [];
            for (let i = headerIdx + 1; i < allRows.length; i++) {
              const obj = {};
              headers.forEach((h, idx) => { obj[h] = (allRows[i][idx] || '').trim(); });
              items.push(obj);
            }
            console.log('First item:', items[0]);
            const result = await importFabrics(items);
            const r = result.results;
            const parts = [];
            if (r.created_types) parts.push(`${r.created_types} new type${r.created_types !== 1 ? 's' : ''}`);
            if (r.created_patterns) parts.push(`${r.created_patterns} new pattern${r.created_patterns !== 1 ? 's' : ''}`);
            if (r.created_colors) parts.push(`${r.created_colors} new color${r.created_colors !== 1 ? 's' : ''}`);
            if (r.updated) parts.push(`${r.updated} updated`);
            if (r.skipped) parts.push(`${r.skipped} skipped`);
            alert(parts.length > 0 ? `Import complete! ${parts.join(', ')}` : 'Import complete! All items already up to date.');
            await fetchAll();
          } catch (err) {
            console.error('Import failed', err);
            alert(`Import failed: ${err.response?.data?.message || err.message || 'Unknown error'}`);
          } finally {
            setImporting(false);
            e.target.value = '';
          }
        }}
      />
      <PageHeader
        title="Fabrics"
        subtitle="Manage fabric types, patterns, and colors"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<FileDownloadRounded />} onClick={async () => {
              try {
                const { items: rows } = await exportFabricsJson();
                await exportRowsToXlsx({
                  rows,
                  columns: [
                    '_id', 'pattern', 'color', 'fabric type', 'grade', 'image',
                    'content', 'backing', 'finish', 'direction', 'repeat', 'weight',
                    'width', 'abrasion', 'physical_properties', 'light_fastness',
                    'color_fastness', 'flammability', 'cleaning',
                  ],
                  filename: 'fabrics',
                  sheetName: 'Fabrics',
                });
              } catch (err) {
                console.error('Export failed:', err);
                alert('Export failed.');
              }
            }}>
              Export
            </Button>
            <Button variant="outlined" startIcon={<UploadFileRounded />}
              onClick={() => document.getElementById('fabrics-import-file')?.click()}
              disabled={importing}>
              {importing ? 'Importing...' : 'Import CSV'}
            </Button>
            <Button variant="outlined" onClick={() => setCollectionsOpen(true)}>
              Collections
            </Button>
            <Button variant="contained" startIcon={<AddRounded />} onClick={() => { setFormName(''); setError(''); setTypeDialog(true); }}>
              Add Type
            </Button>
          </Box>
        }
      />

      {/* Search & Filter Toolbar */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search bar */}
          <TextField
            size="small"
            placeholder="Search patterns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Manufacturer filter */}
          <Autocomplete
            size="small"
            options={allManufacturers}
            getOptionLabel={(o) => o.name}
            value={filterManufacturer}
            onChange={(_, val) => setFilterManufacturer(val)}
            renderInput={(params) => <TextField {...params} label="Manufacturer" />}
            sx={{ minWidth: 200 }}
          />

          {/* Store filter */}
          <Autocomplete
            size="small"
            options={activeStores}
            getOptionLabel={(o) => storeLabel(o)}
            value={filterStore}
            onChange={(_, val) => {
              setFilterStore(val);
              if (!val) setFilterStatus('All');
            }}
            renderInput={(params) => <TextField {...params} label="Store" />}
            sx={{ minWidth: 180 }}
          />

          {/* Status filter */}
          <TextField
            select
            size="small"
            label="Status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            sx={{ minWidth: 140 }}
            disabled={!filterStore}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Published">Published</MenuItem>
            <MenuItem value="Unpublished">Unpublished</MenuItem>
          </TextField>
        </Box>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedPatterns.size > 0 && (
        <Card sx={{ mb: 2, px: 2, py: 1.5, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Checkbox
              checked={allVisibleSelected}
              indeterminate={someVisibleSelected}
              onChange={toggleSelectAll}
              size="small"
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedPatterns.size} pattern{selectedPatterns.size !== 1 ? 's' : ''} selected
            </Typography>

            <Autocomplete
              size="small"
              options={activeStores}
              getOptionLabel={(o) => storeLabel(o)}
              value={bulkStore}
              onChange={(_, val) => setBulkStore(val)}
              renderInput={(params) => <TextField {...params} label="Target Store" />}
              sx={{ minWidth: 180 }}
            />

            <Button
              variant="contained"
              size="small"
              startIcon={bulkProcessing ? <CircularProgress size={16} color="inherit" /> : <PublishRounded />}
              onClick={handleBulkPublish}
              disabled={!bulkStore || bulkProcessing}
            >
              {bulkProcessing && bulkProgress.total > 0
                ? `Pushing ${bulkProgress.done}/${bulkProgress.total}…`
                : 'Push to Shopify'}
            </Button>

            <Button
              variant="contained"
              size="small"
              color="warning"
              startIcon={bulkProcessing ? <CircularProgress size={16} color="inherit" /> : <UnpublishedRounded />}
              onClick={handleBulkUnpublish}
              disabled={!bulkStore || bulkProcessing}
            >
              Unpublish
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => { setAddCollTarget(null); setAddCollNewName(''); setAddCollDialog(true); }}
              disabled={bulkProcessing}
            >
              Add to Collection
            </Button>

            <Button size="small" onClick={clearSelection}>
              Clear selection
            </Button>
            <Button
              variant="contained"
              size="small"
              color="error"
              startIcon={bulkProcessing ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineRounded />}
              onClick={handleBulkDelete}
              disabled={bulkProcessing}
            >
              Delete Selected
            </Button>
          </Box>
        </Card>
      )}

      {/* Column header row — sticky above the fabric type cards so the
          per-store status Selects on each pattern row sit under a labeled
          column instead of looking like floating identical dropdowns.
          Right-side slot widths must match exactly what the pattern row uses. */}
      {types.length > 0 && activeStores.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            mb: 1,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
        >
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem' }}>
            Pattern
          </Typography>
          {activeStores.map((store) => (
            <Box key={store.id} sx={{ width: 110, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                {storeLabel(store)}
              </Typography>
            </Box>
          ))}
          <Box sx={{ width: 132 }} />{/* actions column spacer */}
        </Box>
      )}

      {types.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <TextureRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No fabric types added</Typography>
            <Typography variant="body2" color="text.secondary">Click "Add Type" to create your first fabric type</Typography>
          </Box>
        </Card>
      ) : (
        types.map((type) => {
          const isExpanded = expandedTypes[type.id];
          const typePatterns = patternsForType(type.id);
          return (
            <Card key={type.id} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => toggleType(type.id)}>
                <IconButton size="small" sx={{ mr: 1 }}>
                  {isExpanded ? <ExpandMoreRounded /> : <ChevronRightRounded />}
                </IconButton>
                <Checkbox
                  size="small"
                  checked={typePatterns.length > 0 && typePatterns.every((p) => selectedPatterns.has(p.id))}
                  indeterminate={typePatterns.some((p) => selectedPatterns.has(p.id)) && !typePatterns.every((p) => selectedPatterns.has(p.id))}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSelectedPatterns((prev) => {
                      const next = new Set(prev);
                      const allSelected = typePatterns.every((p) => next.has(p.id));
                      typePatterns.forEach((p) => allSelected ? next.delete(p.id) : next.add(p.id));
                      return next;
                    });
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ p: 0.25 }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>{type.name}</Typography>
                <Chip label={`${typePatterns.length} patterns`} size="small" sx={{ mr: 1 }} />
                <Tooltip title="Add Pattern">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFormName(''); setError(''); setPatternDialog({ open: true, typeId: type.id }); }} sx={{ color: 'primary.main', mr: 0.5 }}>
                    <AddRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Type">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteType(type.id); }} disabled={deleting === `type-${type.id}`} sx={{ color: 'error.main' }}>
                    {deleting === `type-${type.id}` ? <CircularProgress size={16} /> : <DeleteOutlineRounded fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>

              <Collapse in={isExpanded}>
                {typePatterns.length === 0 ? (
                  <Box sx={{ px: 4, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">No patterns yet. Click + to add one.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ px: 1, pb: 1 }}>
                    {typePatterns.map((pattern) => {
                      const patternExpanded = expandedPatterns[pattern.id];
                      const colors = colorsByPattern[pattern.id] || [];
                      const isSelected = selectedPatterns.has(pattern.id);
                      return (
                        <Box key={pattern.id} sx={{ ml: 2, borderLeft: '2px solid', borderColor: 'divider', pl: 1.5, mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', py: 0.75 }}>
                            {/* Checkbox for selection */}
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => togglePatternSelection(pattern.id)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ mr: 0.5, p: 0.25 }}
                            />
                            <IconButton size="small" onClick={() => togglePattern(pattern.id)} sx={{ mr: 0.5 }}>
                              {patternExpanded ? <ExpandMoreRounded fontSize="small" /> : <ChevronRightRounded fontSize="small" />}
                            </IconButton>
                            <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{pattern.name}</Typography>

                            {(pattern.grade_name || pattern.grade) && <Chip label={pattern.grade_name || pattern.grade} size="small" variant="outlined" sx={{ fontSize: '0.7rem', mr: 1 }} />}
                            {pattern.manufacturers.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
                                {pattern.manufacturers.map((m) => <Chip key={m.id} label={m.name} size="small" sx={{ fontSize: '0.65rem' }} />)}
                              </Box>
                            )}
                            <Chip label={`${pattern.color_count} colors`} size="small" sx={{ mr: 1, fontSize: '0.7rem' }} />

                            {/* Per-store Shopify status — one fixed-width column per
                                active store, lined up under the sticky header above.
                                Linked → editable Select (Active / Draft / Archived);
                                unlinked → small grey chip. Widths MUST match the
                                header row (110px per store). */}
                            {activeStores.map((store) => {
                              const link = (pattern.store_statuses || []).find((s) => s.store_id === store.id);
                              const isLinked = !!link?.shopify_product_id;
                              const shopifyStatus = link?.shopify_status || 'unknown';
                              const key = `${pattern.id}-${store.id}`;
                              return (
                                <Box key={store.id} sx={{ width: 110, display: 'flex', justifyContent: 'center' }}>
                                  {isLinked ? (
                                    <FormControl size="small" sx={{ width: 100 }}>
                                      <Select
                                        value={['active','draft','archived'].includes(shopifyStatus) ? shopifyStatus : ''}
                                        onChange={(e) => { e.stopPropagation(); handleShopifyStatusChangeForPattern(pattern.id, store.id, e.target.value); }}
                                        onClick={(e) => e.stopPropagation()}
                                        displayEmpty
                                        disabled={updatingShopifyStatus === key}
                                        sx={{ fontSize: '0.7rem', height: 26, '& .MuiSelect-select': { py: 0.25, pl: 1 } }}
                                      >
                                        <MenuItem value="" disabled><em>Status</em></MenuItem>
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="draft">Draft</MenuItem>
                                        <MenuItem value="archived">Archived</MenuItem>
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <Chip
                                      label="Not linked"
                                      size="small"
                                      sx={{ fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: 'rgba(148,163,184,0.12)', color: '#94A3B8' }}
                                    />
                                  )}
                                </Box>
                              );
                            })}
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditPattern(pattern.id)} sx={{ color: 'primary.main' }}><EditRounded fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Add Color"><IconButton size="small" onClick={() => { setFormName(''); setFormImage(''); setFormColorCode(''); setError(''); setColorDialog({ open: true, patternId: pattern.id }); }} sx={{ color: 'primary.main' }}><AddRounded fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDeletePattern(pattern.id)} disabled={deleting === `pattern-${pattern.id}`} sx={{ color: 'error.main' }}>
                              {deleting === `pattern-${pattern.id}` ? <CircularProgress size={14} /> : <DeleteOutlineRounded fontSize="small" />}
                            </IconButton></Tooltip>
                          </Box>

                          <Collapse in={patternExpanded}>
                            <Box sx={{ pl: 4, pb: 1 }}>
                              {colors.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>No colors yet</Typography>
                              ) : (
                                colors.map((color) => (
                                  <Box key={color.id} sx={{ display: 'flex', alignItems: 'center', py: 0.5, gap: 1.5 }}>
                                    <Avatar variant="rounded" src={color.image_url} sx={{ width: 28, height: 28, bgcolor: 'grey.100' }}>
                                      <ImageRounded sx={{ fontSize: 14, color: 'grey.400' }} />
                                    </Avatar>
                                    <Typography variant="body2" sx={{ flex: 1 }}>{color.name}</Typography>
                                    {color.color_code && (
                                      <Chip label={color.color_code} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    )}
                                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {color.image_url || '--'}
                                    </Typography>
                                    <Tooltip title="Edit Color"><IconButton size="small" onClick={() => openEditColor(color, pattern.id)} sx={{ color: 'primary.main' }}>
                                      <EditRounded sx={{ fontSize: 14 }} />
                                    </IconButton></Tooltip>
                                    <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDeleteColor(color.id, pattern.id)} disabled={deleting === `color-${color.id}`} sx={{ color: 'error.main' }}>
                                      {deleting === `color-${color.id}` ? <CircularProgress size={14} /> : <DeleteOutlineRounded fontSize="small" />}
                                    </IconButton></Tooltip>
                                  </Box>
                                ))
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Collapse>
            </Card>
          );
        })
      )}

      {/* Add Type Dialog */}
      <Dialog open={typeDialog} onClose={() => setTypeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Fabric Type</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Type Name" placeholder="e.g. Velvet" value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1 }} error={!!error} helperText={error || 'Used as a Shopify tag'} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTypeDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateType} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Pattern Dialog */}
      <Dialog open={patternDialog.open} onClose={() => setPatternDialog({ open: false, typeId: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Pattern</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Pattern Name" placeholder="e.g. Solid" value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1 }} error={!!error} helperText={error || 'Used as product name'} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPatternDialog({ open: false, typeId: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePattern} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Color Dialog */}
      <Dialog open={colorDialog.open} onClose={() => setColorDialog({ open: false, patternId: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Color</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Color Name" placeholder="e.g. Navy" value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1, mb: 2 }} error={!!error} helperText={error || 'Used on product title'} />
          <TextField label="Color Code" placeholder="e.g. #FF5733 or RAL 9010" value={formColorCode}
            onChange={(e) => setFormColorCode(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <ImageUploader value={formImage} onChange={setFormImage} folder="fabric-colors" label="Color Image" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setColorDialog({ open: false, patternId: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateColor} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Color Dialog */}
      <Dialog open={editColorDialog.open} onClose={() => setEditColorDialog({ open: false, color: null, patternId: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Color</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Color Name" value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1, mb: 2 }} error={!!error} helperText={error} />
          <TextField label="Color Code" placeholder="e.g. #FF5733 or RAL 9010" value={formColorCode}
            onChange={(e) => setFormColorCode(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <ImageUploader value={formImage} onChange={setFormImage} folder="fabric-colors" label="Color Image" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditColorDialog({ open: false, color: null, patternId: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveColor} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Pattern Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, pattern: null })} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Pattern — {editDialog.pattern?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
            <TextField label="Pattern Name" value={editForm.name || ''} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
            <Autocomplete
              size="small"
              options={allGrades}
              getOptionLabel={(o) => o.name || ''}
              value={allGrades.find((g) => g.id === editForm.fabric_grade_id) || null}
              onChange={(_, val) => setEditForm((f) => ({ ...f, fabric_grade_id: val?.id || null, grade: val?.name || '' }))}
              renderInput={(params) => <TextField {...params} label="Grade" placeholder="Select grade" />}
              sx={{ minWidth: 160 }}
            />
            <TextField select label="Type" value={editForm.fabric_type_id || ''} onChange={(e) => setEditForm((f) => ({ ...f, fabric_type_id: e.target.value }))} sx={{ minWidth: 150 }}>
              {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Autocomplete multiple size="small" options={allManufacturers} getOptionLabel={(o) => o.name}
              value={allManufacturers.filter((m) => editMfrs.includes(m.id))}
              onChange={(_, val) => setEditMfrs(val.map((v) => v.id))}
              renderInput={(params) => <TextField {...params} label="Manufacturers" />} sx={{ flex: 1 }} />
            <Autocomplete multiple size="small" options={allCoatings} getOptionLabel={(o) => o.name}
              value={allCoatings.filter((c) => editCoatings.includes(c.id))}
              onChange={(_, val) => setEditCoatings(val.map((v) => v.id))}
              renderInput={(params) => <TextField {...params} label="Special Coatings" />} sx={{ flex: 1 }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Autocomplete multiple size="small" options={allFeatures} getOptionLabel={(o) => o.name}
              value={allFeatures.filter((f) => editFeatures.includes(f.id))}
              onChange={(_, val) => setEditFeatures(val.map((v) => v.id))}
              renderInput={(params) => <TextField {...params} label="Features" placeholder="Select features (sent as tags)" />}
              sx={{ flex: 1 }} />
            <TextField select label="Status" size="small" value={editForm.status || 'ready'}
              onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
              sx={{ minWidth: 180 }}>
              <MenuItem value="ready">Ready</MenuItem>
              <MenuItem value="needs_attention">Needs Attention</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Autocomplete multiple size="small" options={allCollections} getOptionLabel={(o) => o.name}
              value={allCollections.filter((c) => editCollections.includes(c.id))}
              onChange={(_, val) => setEditCollections(val.map((v) => v.id))}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Collections" placeholder="Assign to fabric collections" />}
              sx={{ flex: 1 }} />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Fabric Details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            {PATTERN_DETAIL_FIELDS.map((f) => (
              <TextField key={f.key} label={f.label} size="small" value={editForm[f.key] || ''}
                onChange={(e) => setEditForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                fullWidth multiline={f.key === 'cleaning'} rows={f.key === 'cleaning' ? 2 : 1} />
            ))}
          </Box>

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialog({ open: false, pattern: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit}>
            {savingEdit ? <CircularProgress size={20} color="inherit" /> : 'Save All'}
          </Button>
        </DialogActions>
      </Dialog>

      <PushResultsDialog
        open={pushResults.open}
        onClose={() => setPushResults({ open: false, results: [] })}
        title="Fabric push results"
        results={pushResults.results}
      />

      <FabricCollectionsDialog open={collectionsOpen} onClose={() => setCollectionsOpen(false)} />

      {/* Add selected patterns to a collection (existing or brand-new) */}
      <Dialog open={addCollDialog} onClose={() => setAddCollDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Add {selectedPatterns.size} pattern{selectedPatterns.size !== 1 ? 's' : ''} to a collection
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            size="small"
            options={allCollections}
            getOptionLabel={(o) => o.name}
            value={addCollTarget}
            onChange={(_, val) => { setAddCollTarget(val); if (val) setAddCollNewName(''); }}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => <TextField {...params} label="Existing collection" placeholder="Choose a collection" />}
            sx={{ mt: 1, mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
            — or create a new one —
          </Typography>
          <TextField
            label="New collection name"
            size="small"
            value={addCollNewName}
            onChange={(e) => { setAddCollNewName(e.target.value); if (e.target.value) setAddCollTarget(null); }}
            fullWidth
            placeholder="e.g. Winter 2026"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddCollDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddToCollection}
            disabled={assigningColl || (!addCollTarget && !addCollNewName.trim())}
          >
            {assigningColl ? <CircularProgress size={20} color="inherit" /> : (addCollTarget ? 'Add' : 'Create & Add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={snack.severity === 'info' ? null : 5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default Fabrics;
